// Pathname based locale patterns
// https://nextjs.org/docs/pages/guides/internationalization#automatic-locale-detection
//https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Accept-Language

import { getCookieMap } from "./cookies";

declare global {
  interface Window {
    OneTrust?: {
      getGeolocationData?: () => { country?: string; state?: string };
    };
  }
}

const PATHNAME_LOCALE_PATTERNS: ReadonlyArray<RegExp> = [
  // Matches locale from ESW routes: /eshopworld/mx/
  /\/eshopworld\/([a-z]{2})\/?/i,
  // Matches locale from Next.js i18n routes: /en-ca/
  /^\/[a-z]{2}-([a-z]{2})\/?/i,
];

const HOSTNAME_LOCALE_PATTERNS: ReadonlyArray<RegExp> = [
  // Matches locale subdomains in production hosts: eu.lululemon.com
  /^(?:www\.)?([a-z]{2})\.lululemon\.com$/i,
  // Matches intl nonprod hosts: de-staging.nonprod-lululemon.com
  /^([a-z]{2,3})-(?:qa|staging)\.nonprod-lululemon\.com$/i,
  // Matches ESW sandbox hosts with a locale subdomain: mx.sandbox.eshopworld.com
  /^([a-z]{2,3})\.sandbox\.eshopworld\.com$/i,
  // Matches ESW application sandbox hosts without a locale subdomain
  /^(?:[a-z0-9-]+\.)?sandbox\.eshopworld\.com$/i,
  // Matches single-part country domains: www.lululemon.de
  /^(?:www\.)?lululemon\.([a-z]{2})$/i,
  // Matches .com country domains: www.lululemon.com.au
  /^(?:www\.)?lululemon\.com\.([a-z]{2})$/i,
  // Matches .co country domains: www.lululemon.co.jp
  /^(?:www\.)?lululemon\.co\.([a-z]{2})$/i,
];

const REGION_ALIASES: Readonly<Record<string, string>> = {
  uk: "gb",
};

const DEFAULT_LULULEMON_COM_HOSTNAME_PATTERN =
  /^(?:[a-z0-9-]+\.)*lululemon\.com$/i;

function extractRegion(
  source: string,
  patterns: ReadonlyArray<RegExp>
): string | null {
  const normalizedSource = source.toLowerCase();

  const match = patterns.find((regex) => {
    return regex.test(normalizedSource);
  });

  if (!match) {
    return null;
  }

  const localeMatch = normalizedSource.match(match);
  const region = localeMatch?.[1];
  return region ? (REGION_ALIASES[region] ?? region) : "unknown";
}

export function getLocaleFromPathname(pathname: string): string | null {
  return extractRegion(pathname, PATHNAME_LOCALE_PATTERNS);
}

function getLocaleFromHostname(hostname: string): string | null {
  const normalizedHostname = hostname.toLowerCase();
  const patternLocale = extractRegion(hostname, HOSTNAME_LOCALE_PATTERNS);
  if (patternLocale) {
    return patternLocale;
  }

  return DEFAULT_LULULEMON_COM_HOSTNAME_PATTERN.test(normalizedHostname)
    ? "us"
    : null;
}

export function getLocaleFromAkamai(): string | null {
  const akamaiCookieValue = getCookieMap()["lll_edge_geo_data"];
  if (akamaiCookieValue) {
    const cookieData = new URLSearchParams(akamaiCookieValue);
    const country = cookieData.get("country");
    return country ? country.toLowerCase() : null;
  }
  return null;
}

export function getStateFromAkamai(): string | null {
  const akamaiCookieValue = getCookieMap()["lll_edge_geo_data"];
  if (akamaiCookieValue) {
    const cookieData = new URLSearchParams(akamaiCookieValue);
    const state = cookieData.get("state");
    return state ? state.toLowerCase() : null;
  }
  return null;
}

export function getLocaleFromOneTrust(): string | null {
  try {
    const otRegion = window.OneTrust?.getGeolocationData?.()?.country;
    return otRegion ? otRegion.toLowerCase() : null;
  } catch {
    return null;
  }
}

export function getStateFromOneTrust(): string | null {
  try {
    const otState = window.OneTrust?.getGeolocationData?.()?.state;
    return otState ? otState.toLowerCase() : null;
  } catch {
    return null;
  }
}

export default function getLocaleFromURL(url: URL | string): string {
  const urlObj = typeof url === "string" ? new URL(url) : url;
  const pathnameLocale = getLocaleFromPathname(urlObj.pathname);
  if (pathnameLocale) {
    return pathnameLocale;
  }

  const hostnameLocale = getLocaleFromHostname(urlObj.hostname);
  if (hostnameLocale) {
    return hostnameLocale;
  }

  return "";
}
